import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { SelectButton } from "primereact/selectbutton";
import { classNames } from "primereact/utils";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Fetch } from "../../shared/Fetch";
import RichTextEditor from "../RichEditorText/richEditorText";
import styled from "styled-components";

const SignaturePreview = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 10px 12px;
  font-family: inherit;
  color: #495057;
  font-size: 0.875rem;
  min-height: 38px;
  margin-top: 4px;
`;

const ErrorText = styled.small`
  font-size: 0.75rem;
  color: #dc2626;
  margin: 4px 0 0 0 !important;
  display: block;
`;

const FormContainer = styled.form`
  .p-selectbutton {
    display: inline-flex;
  }
  .p-selectbutton .p-button {
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 1.25rem;
  }
`;

const schema = yup.object({
  subject: yup.string().required("Subject is required"),
  body: yup
    .string()
    .required("Mail Body is required")
    .test("is-empty", "Mail Body is required", (value) => {
      if (!value) return false;
      const cleanText = value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .replace(/[\s\u200b\ufeff\u2000-\u200d\u202f\u205f\u3000]/g, "")
        .trim();
      return cleanText.length > 0;
    }),
});

interface EmailComposeDialogProps {
  visible: boolean;
  onHide: () => void;
  lead: any;
  toast: any;
  onSuccess: (emailSent: boolean, description: string) => void;
}

export const EmailComposeDialog: React.FC<EmailComposeDialogProps> = ({
  visible,
  onHide,
  lead,
  toast,
  onSuccess,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [showSignature, setShowSignature] = useState(true);
  const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
  const [userSignatures, setUserSignatures] = useState({
    shortSignature: "",
    fullSignature: "",
  });
  const [sending, setSending] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      subject: "",
      body: "",
    },
  });

  useEffect(() => {
    if (visible) {
      reset({
        subject: "",
        body: "",
      });
      setFiles([]);
      setShowSignature(true);
      setSignatureType("Full");
      loadUserSignatures();
    }
  }, [visible, reset]);

  const loadUserSignatures = async () => {
    try {
      const res = await Fetch("UserSignature", "GET", null, null, null);
      if (res) {
        const data = await res.json();
        setUserSignatures({
          shortSignature: data.shortSignature || "",
          fullSignature: data.fullSignature || "",
        });
      }
    } catch (err) {
      console.error("Failed to load user signatures", err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSend = async (data: any) => {
    const cleanText = (data.body || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/[\s\u200b\ufeff\u2000-\u200d\u202f\u205f\u3000]/g, "")
      .trim();

    if (!cleanText) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Mail Body is required",
        life: 3000,
      });
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("subject", data.subject);
      formData.append("body", data.body);
      formData.append("signatureType", showSignature ? signatureType : "None");
      
      files.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await Fetch(
        `LeadAllocation/my-leads/${lead.id}/send-email`,
        "POST",
        formData,
        null,
        toast
      );

      if (res) {
        toast.current?.show({
          severity: "success",
          summary: "Email Sent",
          detail: "Email sent successfully and recorded in history.",
          life: 3000,
        });
        onSuccess(true, data.body);
        onHide();
      }
    } catch (err) {
      console.error("Failed to send email", err);
    } finally {
      setSending(false);
    }
  };

  const signatureOptions = [
    { label: "Short Signature", value: "Short" },
    { label: "Full Signature", value: "Full" },
  ];

  const currentSignatureText =
    signatureType === "Short"
      ? userSignatures.shortSignature
      : userSignatures.fullSignature;

  return (
    <Dialog
      header={`Compose Email to ${lead?.contactName || lead?.contact_Person?.contact_Person_Name || "Recipient"}`}
      visible={visible}
      style={{ width: "800px" }}
      modal
      onHide={onHide}
      className="p-fluid"
    >
      <FormContainer onSubmit={handleSubmit(handleSend)} className="flex flex-column gap-3 mt-2">
        <div className="flex flex-column gap-1">
          <label htmlFor="recipient-email" className="font-semibold text-sm">
            To
          </label>
          <InputText
            id="recipient-email"
            value={lead?.email || lead?.contact_Person?.contact_Person_Email || ""}
            disabled
            className="bg-gray-100"
          />
        </div>

        <div className="flex flex-column gap-1">
          <label htmlFor="email-subject" className="font-semibold text-sm">
            Subject <span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name="subject"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                id="email-subject"
                placeholder="Enter email subject"
                className={classNames({ "p-invalid": fieldState.invalid })}
                autoFocus
              />
            )}
          />
          {errors.subject && (
            <ErrorText className="p-error">{errors.subject.message}</ErrorText>
          )}
        </div>

        <div className="flex flex-column gap-1">
          <label className="font-semibold text-sm">
            Mail Body <span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name="body"
            control={control}
            render={({ field, fieldState }) => (
              <div className={classNames({ "p-invalid": fieldState.invalid })}>
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  height={300}
                />
              </div>
            )}
          />
          {errors.body && (
            <ErrorText className="p-error">{errors.body.message}</ErrorText>
          )}
        </div>

        <div className="flex flex-column gap-1">
          <label className="font-semibold text-sm">Attachments</label>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
          <div className="flex gap-2 align-items-center">
            <Button
              type="button"
              label="Choose Files"
              icon="pi pi-paperclip"
              className="p-button-outlined p-button-secondary w-auto p-button-sm"
              onClick={() => fileInputRef.current?.click()}
            />
            {files.length > 0 && (
              <span className="text-sm text-color-secondary">
                {files.length} file(s) selected
              </span>
            )}
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {files.map((file, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 border-round flex align-items-center gap-1"
                >
                  {file.name}
                  <i
                    className="pi pi-times cursor-pointer text-xs"
                    onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                  />
                </span>
              ))}
            </div>
          )}
        </div>

        <hr className="surface-border my-2" />

        <div className="flex flex-column gap-2">
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="show-sig-checkbox"
              checked={showSignature}
              onChange={(e) => setShowSignature(e.checked ?? false)}
            />
            <label htmlFor="show-sig-checkbox" className="font-semibold text-sm cursor-pointer">
              Show Signature
            </label>
          </div>

          {showSignature && (
            <div className="flex flex-column gap-2 ml-4">
              <SelectButton
                value={signatureType}
                options={signatureOptions}
                onChange={(e) => e.value && setSignatureType(e.value)}
                className="w-max select-button-sm text-xs"
              />
              {currentSignatureText ? (
                <SignaturePreview
                  dangerouslySetInnerHTML={{ __html: currentSignatureText }}
                />
              ) : (
                <div className="text-xs text-color-secondary italic">
                  No signature defined. Update it in Email Signature settings.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text p-button-secondary w-auto"
            onClick={onHide}
            disabled={sending}
          />
          <Button
            type="submit"
            label="Send Email"
            icon="pi pi-send"
            loading={sending}
            className="w-auto p-button-primary"
          />
        </div>
      </FormContainer>
    </Dialog>
  );
};
